import { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";

export const useOverflowItems = (items, { gap = 8, badgeWidth = 40 } = {}) => {
	const [visibleCount, setVisibleCount] = useState(items.length);
	const containerRef = useRef(null);
	const itemWidthsRef = useRef([]);

	const calculate = useCallback(
		(containerWidth) => {
			const widths = itemWidthsRef.current;
			if (widths.length === 0) return;

			let totalWidth = 0;
			for (let i = 0; i < widths.length; i++) {
				totalWidth += widths[i] + (i > 0 ? gap : 0);
			}

			if (totalWidth <= containerWidth) {
				setVisibleCount(items.length);
				return;
			}

			let usedWidth = 0;
			let count = 0;
			for (let i = 0; i < widths.length; i++) {
				const needed = widths[i] + (i > 0 ? gap : 0);
				if (usedWidth + needed + gap + badgeWidth <= containerWidth) {
					usedWidth += needed;
					count++;
				} else {
					break;
				}
			}

			setVisibleCount(Math.max(count, 0));
		},
		[items.length, gap, badgeWidth],
	);

	useLayoutEffect(() => {
		if (!containerRef.current || items.length === 0) return;
		setVisibleCount(items.length);
	}, [items.length]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container || items.length === 0) return;
		if (visibleCount !== items.length) return;

		const children = Array.from(container.querySelectorAll("[data-tag]"));
		children.forEach((child, i) => {
			itemWidthsRef.current[i] = child.offsetWidth;
		});

		calculate(container.clientWidth);
	}, [visibleCount, items.length, calculate]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || items.length === 0) return;

		const observer = new ResizeObserver(() => {
			calculate(container.clientWidth);
		});
		observer.observe(container);

		return () => observer.disconnect();
	}, [items.length, calculate]);

	const hiddenCount = items.length - visibleCount;
	const displayedItems =
		visibleCount === items.length ? items : items.slice(0, visibleCount);

	return { containerRef, displayedItems, hiddenCount };
};
